/**
 * Enhanced Error Handling Utilities
 * Multi-Tenant API Error Management
 *
 * @description Issue #283 対応 - backend/エラー形式完全対応
 * @backend NestJS ErrorResponse統合
 * @security セキュリティエラー分類・ユーザーフレンドリー対応
 */

import logger from './logger';

// Multi-Tenant Error Types
import type {
  MultiTenantApiError,
} from '../types/multiTenant';

// Runtime value imports
import { SecurityErrorType } from '../types/multiTenant';

// ====================================
// Error Classification & Mapping
// ====================================

/**
 * User-friendly error messages mapping
 */
export const ERROR_MESSAGES: Record<string, string> = {
  // Authentication Errors
  AUTHENTICATION_FAILED: 'ログインに失敗しました。メールアドレスまたはパスワードをご確認ください。',
  AUTHORIZATION_FAILED: 'この操作を実行する権限がありません。',
  INVALID_TOKEN: 'セッションが無効です。再度ログインしてください。',
  SESSION_EXPIRED: 'セッションの有効期限が切れました。再度ログインしてください。',
  PERMISSION_DENIED: 'この機能にアクセスする権限がありません。',

  // Tenant-specific Errors
  TENANT_ACCESS_VIOLATION: 'テナントアクセス違反が検出されました。システム管理者にお問い合わせください。',
  PLAN_RESTRICTION: 'この機能は現在のプランでは利用できません。プランをアップグレードしてください。',
  FEATURE_UNAVAILABLE: 'この機能は現在利用できません。',

  // Rate Limiting
  RATE_LIMIT_EXCEEDED: 'リクエスト数が上限に達しました。しばらくお待ちください。',

  // Security
  SUSPICIOUS_ACTIVITY: 'セキュリティ上の理由により、一時的にアカウントが制限されています。',

  // Network & Server Errors
  NETWORK_ERROR: 'ネットワークエラーが発生しました。接続をご確認ください。',
  SERVER_ERROR: 'サーバーエラーが発生しました。しばらくしてから再度お試しください。',
  SERVICE_UNAVAILABLE: 'サービスが一時的に利用できません。しばらくしてから再度お試しください。',

  // Validation Errors
  VALIDATION_ERROR: '入力内容に不備があります。内容をご確認ください。',
  NOT_FOUND: '指定されたリソースが見つかりませんでした。',
  CONFLICT: '競合するデータが存在します。',

  // Default
  UNKNOWN_ERROR: '不明なエラーが発生しました。',
};

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

/**
 * Error severity mapping based on security error types
 */
export const ERROR_SEVERITY_MAP: Record<SecurityErrorType, ErrorSeverity> = {
  [SecurityErrorType.AUTHENTICATION_FAILED]: ErrorSeverity.MEDIUM,
  [SecurityErrorType.AUTHORIZATION_FAILED]: ErrorSeverity.MEDIUM,
  [SecurityErrorType.TENANT_ACCESS_VIOLATION]: ErrorSeverity.CRITICAL,
  [SecurityErrorType.RATE_LIMIT_EXCEEDED]: ErrorSeverity.LOW,
  [SecurityErrorType.INVALID_TOKEN]: ErrorSeverity.MEDIUM,
  [SecurityErrorType.SESSION_EXPIRED]: ErrorSeverity.LOW,
  [SecurityErrorType.PERMISSION_DENIED]: ErrorSeverity.MEDIUM,
  [SecurityErrorType.PLAN_RESTRICTION]: ErrorSeverity.LOW,
  [SecurityErrorType.FEATURE_UNAVAILABLE]: ErrorSeverity.LOW,
  [SecurityErrorType.SUSPICIOUS_ACTIVITY]: ErrorSeverity.HIGH,
};

// ====================================
// Enhanced Error Classes
// ====================================

/**
 * Enhanced API Error class with multi-tenant support
 */
export class EnhancedApiError extends Error {
  public readonly statusCode: number;
  public readonly error: string; // Required by MultiTenantApiError interface
  public readonly errorType: string; // Legacy property for backward compatibility
  public readonly timestamp: string;
  public readonly path: string;
  public readonly requestId?: string;
  public readonly tenantId?: string;
  public readonly correlationId?: string;
  public readonly details?: {
    securityErrorType?: SecurityErrorType;
    suggestions?: string[];
    [key: string]: unknown;
  };
  public readonly severity: ErrorSeverity;
  public readonly userMessage: string;
  public readonly isRetryable: boolean;

  constructor(error: MultiTenantApiError) {
    super(typeof error.message === 'string' ? error.message : error.message.join(', '));

    this.name = 'EnhancedApiError';
    this.statusCode = error.statusCode;
    this.error = error.error; // Required by MultiTenantApiError interface
    this.errorType = error.error; // Legacy property for backward compatibility
    this.timestamp = error.timestamp;
    this.path = error.path;
    this.requestId = error.requestId;
    this.tenantId = error.tenantId;
    this.correlationId = error.correlationId;
    this.details = error.details;

    // Determine severity
    this.severity = this.determineSeverity(error);

    // Get user-friendly message
    this.userMessage = this.getUserFriendlyMessage(error);

    // Determine if error is retryable
    this.isRetryable = this.determineRetryability(error);

    // Maintain proper prototype chain
    Object.setPrototypeOf(this, EnhancedApiError.prototype);
  }

  private determineSeverity(error: MultiTenantApiError): ErrorSeverity {
    if (error.details?.securityErrorType) {
      return ERROR_SEVERITY_MAP[error.details.securityErrorType] || ErrorSeverity.MEDIUM;
    }

    // Fallback based on status code
    if (error.statusCode >= 500) return ErrorSeverity.HIGH;
    if (error.statusCode === 401 || error.statusCode === 403) return ErrorSeverity.MEDIUM;
    if (error.statusCode === 429) return ErrorSeverity.LOW;
    return ErrorSeverity.MEDIUM;
  }

  private getUserFriendlyMessage(error: MultiTenantApiError): string {
    // Check for security error type first
    if (error.details?.securityErrorType) {
      const message = ERROR_MESSAGES[error.details.securityErrorType];
      if (message) return message;
    }

    // Check for general error type
    const generalMessage = ERROR_MESSAGES[error.error];
    if (generalMessage) return generalMessage;

    // Check for status code based message
    if (error.statusCode === 400) return ERROR_MESSAGES.VALIDATION_ERROR;
    if (error.statusCode === 401) return ERROR_MESSAGES.AUTHENTICATION_FAILED;
    if (error.statusCode === 403) return ERROR_MESSAGES.AUTHORIZATION_FAILED;
    if (error.statusCode === 404) return ERROR_MESSAGES.NOT_FOUND;
    if (error.statusCode === 409) return ERROR_MESSAGES.CONFLICT;
    if (error.statusCode === 429) return ERROR_MESSAGES.RATE_LIMIT_EXCEEDED;
    if (error.statusCode >= 500) return ERROR_MESSAGES.SERVER_ERROR;

    return ERROR_MESSAGES.UNKNOWN_ERROR;
  }

  private determineRetryability(error: MultiTenantApiError): boolean {
    // Server errors (5xx) are generally retryable
    if (error.statusCode >= 500) return true;

    // Rate limiting is retryable after delay
    if (error.statusCode === 429) return true;

    // Network errors are retryable
    if (error.error === 'NETWORK_ERROR') return true;

    // Auth/permission errors are not retryable
    if (error.statusCode === 401 || error.statusCode === 403) return false;

    // Validation errors are not retryable
    if (error.statusCode === 400) return false;

    return false;
  }

  /**
   * Get suggestions for resolving the error
   */
  getSuggestions(): string[] {
    // Type guard for details with suggestions
    if (
      this.details &&
      typeof this.details === 'object' &&
      'suggestions' in this.details &&
      Array.isArray(this.details.suggestions)
    ) {
      return this.details.suggestions;
    }

    const suggestions: string[] = [];

    // Type guard for securityErrorType
    const securityErrorType = (
      this.details &&
      typeof this.details === 'object' &&
      'securityErrorType' in this.details
    ) ? this.details.securityErrorType : null;

    switch (securityErrorType || this.errorType) {
      case SecurityErrorType.AUTHENTICATION_FAILED:
        suggestions.push('メールアドレスとパスワードを再確認してください');
        suggestions.push('パスワードを忘れた場合は、リセット機能をご利用ください');
        break;

      case SecurityErrorType.AUTHORIZATION_FAILED:
      case SecurityErrorType.PERMISSION_DENIED:
        suggestions.push('アカウント管理者に権限の確認をご依頼ください');
        suggestions.push('必要な権限が付与されているかご確認ください');
        break;

      case SecurityErrorType.PLAN_RESTRICTION:
        suggestions.push('プランをアップグレードすることで機能が利用可能になります');
        suggestions.push('料金プランの詳細についてはサポートまでお問い合わせください');
        break;

      case SecurityErrorType.RATE_LIMIT_EXCEEDED:
        suggestions.push('少し時間をおいてから再度お試しください');
        suggestions.push('短時間に大量のリクエストを送信しないでください');
        break;

      case SecurityErrorType.SESSION_EXPIRED:
        suggestions.push('再度ログインしてください');
        suggestions.push('ログイン状態を維持したい場合は、「ログイン状態を保持する」をチェックしてください');
        break;

      case 'NETWORK_ERROR':
        suggestions.push('インターネット接続をご確認ください');
        suggestions.push('ファイアウォールやプロキシ設定をご確認ください');
        break;

      case 'SERVER_ERROR':
        suggestions.push('しばらく時間をおいてから再度お試しください');
        suggestions.push('問題が続く場合は、サポートまでお問い合わせください');
        break;

      default:
        suggestions.push('問題が続く場合は、サポートまでお問い合わせください');
        break;
    }

    return suggestions;
  }

  /**
   * Convert to loggable object (without sensitive information)
   */
  toLogObject(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      statusCode: this.statusCode,
      errorType: this.errorType,
      severity: this.severity,
      timestamp: this.timestamp,
      path: this.path,
      requestId: this.requestId,
      tenantId: this.tenantId,
      correlationId: this.correlationId,
      isRetryable: this.isRetryable,
      // Don't log sensitive details
      hasDetails: !!this.details,
    };
  }
}

// ====================================
// Error Handling Utilities
// ====================================

/**
 * Create enhanced error from MultiTenantApiError
 */
export function createEnhancedError(error: MultiTenantApiError): EnhancedApiError {
  return new EnhancedApiError(error);
}

/**
 * Handle and transform any error to EnhancedApiError
 */
export function handleError(error: unknown): EnhancedApiError {
  // Already an EnhancedApiError
  if (error instanceof EnhancedApiError) {
    return error;
  }

  // MultiTenantApiError
  if (error && typeof error === 'object' && 'statusCode' in error) {
    return new EnhancedApiError(error as MultiTenantApiError);
  }

  // Network errors (type guard for Error objects)
  if (error instanceof Error && error.name === 'TypeError' && error.message.includes('fetch')) {
    const networkError: MultiTenantApiError = {
      statusCode: 0,
      error: 'NETWORK_ERROR',
      message: 'Network connection failed',
      timestamp: new Date().toISOString(),
      path: '',
    };
    return new EnhancedApiError(networkError);
  }

  // Generic error
  const errorMessage = error instanceof Error ? error.message :
                      typeof error === 'string' ? error :
                      'Unknown error occurred';

  const genericError: MultiTenantApiError = {
    statusCode: 500,
    error: 'UNKNOWN_ERROR',
    message: errorMessage,
    timestamp: new Date().toISOString(),
    path: '',
  };
  return new EnhancedApiError(genericError);
}

/**
 * Log error with appropriate level based on severity
 */
export function logError(error: EnhancedApiError, context?: Record<string, unknown>): void {
  const logData = {
    ...error.toLogObject(),
    ...context,
  };

  switch (error.severity) {
    case ErrorSeverity.CRITICAL:
      logger.error('[CRITICAL ERROR]', logData);
      break;
    case ErrorSeverity.HIGH:
      logger.error('[HIGH SEVERITY ERROR]', logData);
      break;
    case ErrorSeverity.MEDIUM:
      logger.warn('[MEDIUM SEVERITY ERROR]', logData);
      break;
    case ErrorSeverity.LOW:
      logger.log('[LOW SEVERITY ERROR]', logData);
      break;
    default:
      logger.error('[ERROR]', logData);
      break;
  }
}

/**
 * Check if error should trigger automatic logout
 */
export function shouldLogoutOnError(error: EnhancedApiError): boolean {
  // Type guard for securityErrorType
  const securityErrorType = (
    error.details &&
    typeof error.details === 'object' &&
    'securityErrorType' in error.details
  ) ? error.details.securityErrorType : null;

  return (
    error.statusCode === 401 ||
    securityErrorType === SecurityErrorType.INVALID_TOKEN ||
    securityErrorType === SecurityErrorType.SESSION_EXPIRED ||
    securityErrorType === SecurityErrorType.TENANT_ACCESS_VIOLATION
  );
}

/**
 * Check if error should be displayed to user
 */
export function shouldDisplayToUser(error: EnhancedApiError): boolean {
  // Don't display highly technical errors
  if (error.severity === ErrorSeverity.CRITICAL) {
    return false;
  }

  // Don't display errors that are already handled by app logic
  if (error.details?.securityErrorType === SecurityErrorType.SESSION_EXPIRED) {
    return false;
  }

  return true;
}

/**
 * Get retry delay based on error type and attempt number
 */
export function getRetryDelay(error: EnhancedApiError, attempt: number): number {
  if (!error.isRetryable) return 0;

  // Rate limit specific delay
  if (error.statusCode === 429) {
    // Check for Retry-After header in details (type guard)
    const retryAfter = (
      error.details &&
      typeof error.details === 'object' &&
      'retryAfter' in error.details &&
      typeof error.details.retryAfter === 'number'
    ) ? error.details.retryAfter as number : null;

    if (retryAfter) {
      return retryAfter * 1000; // Convert to milliseconds
    }
    return Math.min(60000, 1000 * Math.pow(2, attempt)); // Max 1 minute
  }

  // Exponential backoff for server errors
  if (error.statusCode >= 500) {
    return Math.min(30000, 1000 * Math.pow(2, attempt)); // Max 30 seconds
  }

  // Default retry delay
  return Math.min(10000, 1000 * Math.pow(2, attempt)); // Max 10 seconds
}

// ====================================
// Error Boundary Utilities
// ====================================

/**
 * Error boundary fallback component props
 */
export interface ErrorFallbackProps {
  error: EnhancedApiError;
  resetError: () => void;
  retry?: () => void;
}

/**
 * Create error info for error boundaries
 */
export function createErrorInfo(error: unknown, errorInfo?: unknown): {
  enhancedError: EnhancedApiError;
  componentStack?: string;
} {
  const enhancedError = handleError(error);

  // Type guard for componentStack
  const componentStack = (
    errorInfo &&
    typeof errorInfo === 'object' &&
    'componentStack' in errorInfo &&
    typeof (errorInfo as Record<string, unknown>).componentStack === 'string'
  ) ? (errorInfo as Record<string, unknown>).componentStack as string : undefined;

  return {
    enhancedError,
    componentStack,
  };
}

// ====================================
// Export
// ====================================

export default {
  EnhancedApiError,
  createEnhancedError,
  handleError,
  logError,
  shouldLogoutOnError,
  shouldDisplayToUser,
  getRetryDelay,
  createErrorInfo,
  ERROR_MESSAGES,
  ErrorSeverity,
  ERROR_SEVERITY_MAP,
};